// libs
import { useContext, useRef } from "react";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { UseMutationResult, UseQueryResult, useMutation, useQuery, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
// utils
import {
    Sim,
    InputType,
    UpdateSimRequest,
    CreateSimRequest,
    AllocationType,
    CommentaryStatus,
    CreateLinkInputMutationVariables,
    Scenario,
    ShareSimToRFAMutationVariables,
    PublishSimMutationVariables,
} from "../../utils/appsync/schema/API";
import { measureLatency } from "@helpers/index";
import { formatGraphQLError } from "@helpers/Error";
import { buildSimulationFilters } from "@utils/index";
import { getClient, graphQlMutations, graphQlQueries } from "@utils/appsync";

// stores
import { AppContext } from "@store/context/App";

// defs
import { GridFilterItem } from "@mui/x-data-grid-pro";

// hooks
import { useLogger } from "@hooks/useLogger";
import { useMetrics } from "@hooks/useMetrics";

// constants
import { MetricName } from "@constants/Metrics";
import { ServiceType } from "@constants/Services";
import { SharedSimulationKeys } from "@constants/index";

/**
 * Hook to fetch the maximum period available for Manual Journal Entry (MJE) simulations
 * for a given scenario. This is used to determine the time range constraints for GL simulations.
 *
 * @param scenario - The scenario name to fetch max period for
 * @returns Query result containing the maximum period string
 */
export const useGetMaxPeriodForDataSets = (scenario: string): UseQueryResult<string, Error> => {
    const { publishErrorLog } = useLogger();
    const { publishFailureMetric, publishLatencyMetric } = useMetrics();

    return useQuery<string, Error>({
        queryKey: ["GetMaxPeriodForDataSets", scenario],
        queryFn: async () => {
            const startTime = Date.now();

            const { data, errors }: GraphQLResult<any> = await getClient().graphql({
                query: graphQlQueries.GetMaxPeriodForDataSets,
                variables: {
                    input: { scenario },
                },
            });

            publishLatencyMetric(MetricName.GET_MAX_PERIOD_LATENCY, measureLatency(startTime), ServiceType.SimulationService);

            if (errors?.length) {
                publishErrorLog(errors.map((error) => formatGraphQLError(error)));
                publishFailureMetric(MetricName.GET_MAX_PERIOD_ERROR_COUNT, ServiceType.SimulationService);
            }

            return data?.GetMaxPeriodForDataSets?.maxPeriodForDataSets;
        },
        enabled: !!scenario?.length,
    });
};

export const useGetScenariosList = (
    allocation_type: AllocationType,
    is_active: boolean = true,
    is_enabled: boolean = true,
    enableFetch: boolean = true,
): UseQueryResult<Array<Scenario>, Error> => {
    const { publishErrorLog } = useLogger();

    const { publishFailureMetric, publishLatencyMetric } = useMetrics();

    return useQuery<Array<Scenario>, Error>({
        queryKey: ["ListScenarios", allocation_type],
        queryFn: async () => {
            const startTime = Date.now();

            const { data, errors }: GraphQLResult<any> = await getClient().graphql({
                query: graphQlQueries.ListScenarios,
                variables: {
                    allocation_type,
                    is_active,
                    is_enabled,
                },
            });

            // publish the ListScenarios service latency metrics
            publishLatencyMetric(MetricName.GET_SCENARIOS_LIST_LATENCY, measureLatency(startTime), ServiceType.SimulationService);

            // in case of ListScenarios querry failure, publish the logs and metrics to cloudwatch
            if (errors?.length) {
                // publishing the error logs
                publishErrorLog(errors.map((error) => formatGraphQLError(error)));

                // publish the failure metrics
                publishFailureMetric(MetricName.GET_SCENARIOS_LIST_ERROR_COUNT, ServiceType.SimulationService);
            }

            return data?.ListScenarios?.items ?? [];
        },
        enabled: !!allocation_type && enableFetch,
        staleTime: 30 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    });
};

// Type for the page structure returned by infinite query
interface SimulationQueryPage {
    items: Array<Sim>;
    nextToken: string | null;
    hasData: boolean;
}
export const useInfiniteSimulationList = (
    sandbox_id: string,
    limit: number = 100,
    filters?: Array<GridFilterItem>,
): ReturnType<typeof useInfiniteQuery<SimulationQueryPage, Error>> => {
    const { publishErrorLog } = useLogger();
    const { publishFailureMetric, publishLatencyMetric } = useMetrics();
    return useInfiniteQuery<SimulationQueryPage, Error>({
        queryKey: ["sandboxList", sandbox_id, filters, limit],
        initialPageParam: null,
        queryFn: async ({ pageParam = null }) => {
            const startTime = Date.now();
            try {
                const { data, errors }: GraphQLResult<any> = await getClient().graphql({
                    query: graphQlQueries.ListSimsBySandbox,
                    variables: {
                        sandbox_id,
                        is_shared_by_me:
                            filters?.find((eachFilter) => eachFilter.field === SharedSimulationKeys.IS_SHARED_BY_ME)?.value ?? undefined,
                        is_shared: filters?.find((eachFilter) => eachFilter.field === SharedSimulationKeys.IS_SHARED)?.value ?? undefined,
                        limit,
                        nextToken: pageParam,
                        filter: buildSimulationFilters(filters),
                    },
                });
                const items = data?.ListSimsBySandbox?.items?.filter((val: Sim | null) => val != null) || [];
                const nextToken = data?.ListSimsBySandbox?.nextToken ?? null;
                const hasData = items.length > 0;
                // publish the ListSimsBySandbox service latency metrics
                publishLatencyMetric(MetricName.GET_SIMULATION_LIST_LATENCY, measureLatency(startTime), ServiceType.SimulationService);

                // in case of ListSimsBySandbox querry failure, publish the logs and metrics to cloudwatch
                if (errors?.length) {
                    // publishing the error logs
                    publishErrorLog(errors.map((error) => formatGraphQLError(error)));

                    // publish the failure metrics
                    publishFailureMetric(MetricName.GET_SIMULATION_LIST_ERROR_COUNT, ServiceType.SimulationService);
                }
                return { items, nextToken, hasData };
            } catch (error) {
                // GraphQL client threw an error - check if error object contains partial data
                const errorData = (error as any)?.data;
                const errorItems = errorData?.ListSimsBySandbox?.items?.filter((val: Sim | null) => val != null) || [];
                const hasPartialData = errorItems.length > 0;

                if (hasPartialData) {
                    // Partial failure: Error was thrown but contains data - return gracefully
                    console.warn("GraphQL threw error but error object contains partial data:", error);
                    publishErrorLog([formatGraphQLError(error)]);
                    return {
                        items: errorItems,
                        nextToken: errorData?.ListSimsBySandbox?.nextToken ?? null,
                        hasData: true,
                    };
                } else {
                    // Complete failure: No data at all
                    console.error("GraphQL error with no data:", error);
                    publishErrorLog([formatGraphQLError(error)]);
                    publishFailureMetric(MetricName.GET_SIMULATION_LIST_ERROR_COUNT, ServiceType.SimulationService);
                    throw error;
                }
            }
        },
        getNextPageParam: (lastPage) => {
            // Only return nextToken if there's actually more data
            return lastPage.nextToken && lastPage.hasData ? lastPage.nextToken : undefined;
        },
        enabled: !!sandbox_id,
        refetchOnWindowFocus: false,
        retry: false,
    });
};

export const useGetSimulation = (
    sandbox_id: string,
    sim_id: string,
    is_shared: Boolean = false,
    skipGlobalLoader: boolean = false,
    skipFetch: boolean = false,
): UseQueryResult<Sim, Error> => {
    const { startLoading, stopLoading } = useContext(AppContext);

    const { publishErrorLog } = useLogger();

    const { publishFailureMetric, publishLatencyMetric } = useMetrics();

    return useQuery<Sim, Error>({
        queryKey: ["simulation", sandbox_id, sim_id, is_shared],
        queryFn: async () => {
            const startTime = Date.now();

            if (!skipGlobalLoader) {
                startLoading();
            }

            const { data, errors }: GraphQLResult<any> = await getClient().graphql({
                query: graphQlQueries.GetSimDetails,
                variables: {
                    sandbox_id,
                    sim_id,
                    is_active: true,
                    is_shared,
                },
            });

            if (!skipGlobalLoader) {
                stopLoading();
            }

            // publish the GetSimDetails service latency metrics
            publishLatencyMetric(MetricName.GET_SIMULATION_LATENCY, measureLatency(startTime), ServiceType.SimulationService);

            // in case of GetSimDetails querry failure, publish the logs and metrics to cloudwatch
            if (errors?.length) {
                // publishing the error logs
                publishErrorLog(errors.map((error) => formatGraphQLError(error)));

                // publish the failure metrics
                publishFailureMetric(MetricName.GET_SIMULATION_ERROR_COUNT, ServiceType.SimulationService);
            }
            return data.GetSimDetails;
        },
        enabled: !!sandbox_id && !!sim_id?.length && !skipFetch,
        refetchInterval: (query) =>
            query.state.data?.commentary_status === CommentaryStatus.IN_PROGRESS ? 60_000 : false,
        staleTime: 60_000,
    });
};

export const useCreateSimulation = (): UseMutationResult<Sim, Error, { sandbox_id: string; request: CreateSimRequest }> => {
    const { publishErrorLog } = useLogger();

    const { publishFailureMetric, publishLatencyMetric } = useMetrics();

    return useMutation<Sim, Error, { sandbox_id: string; sim_id: string; request: CreateSimRequest }>({
        mutationFn: async ({ sandbox_id, sim_id, request }) => {
            const startTime = Date.now();

            const { data, errors }: GraphQLResult<any> = await getClient().graphql({
                query: graphQlMutations.CreateSim,
                variables: {
                    request,
                    sandbox_id,
                },
            });

            // publish the CreateSim service latency metrics
            publishLatencyMetric(MetricName.CREATE_SIMULATION_LATENCY, measureLatency(startTime), ServiceType.SimulationService);

            // in case of CreateSim querry failure, publish the logs and metrics to cloudwatch
            if (errors?.length) {
                // publishing the error logs
                publishErrorLog(errors.map((error) => formatGraphQLError(error)));

                // publish the failure metrics
                publishFailureMetric(MetricName.CREATE_SIMULATION_ERROR_COUNT, ServiceType.SimulationService);
            }

            return data.CreateSim;
        },
    });
};

export const useSaveSimulation = (): UseMutationResult<
    Sim,
    Error,
    { sandbox_id: string; sim_id: string; request: CreateSimRequest | UpdateSimRequest }
> => {
    const client = getClient();

    const { publishErrorLog } = useLogger();

    const saveSimulationRef = useRef<Promise<GraphQLResult<any>>>(null);

    const { publishFailureMetric, publishLatencyMetric } = useMetrics();

    return useMutation<Sim, Error, { sandbox_id: string; sim_id: string; request: CreateSimRequest | UpdateSimRequest }>({
        mutationFn: async ({ sandbox_id, sim_id, request }) => {
            const startTime = Date.now();

            // Cancel the previous request
            client.cancel(saveSimulationRef.current);

            if (sim_id) {
                saveSimulationRef.current = client.graphql({
                    query: graphQlMutations.UpdateSim,
                    variables: {
                        request,
                        sandbox_id,
                        sim_id,
                    },
                });

                const { data, errors } = await saveSimulationRef.current;

                // publish the Save Sim service latency metrics
                publishLatencyMetric(MetricName.UPDATE_SIMULATION_LATENCY, measureLatency(startTime), ServiceType.SimulationService);

                // in case of Save Sim mutation failure, publish the logs and metrics to cloudwatch
                if (errors?.length) {
                    // publishing the error logs
                    publishErrorLog(errors.map((error) => formatGraphQLError(error)));

                    // publish the failure metrics
                    publishFailureMetric(MetricName.UPDATE_SIMULATION_ERROR_COUNT, ServiceType.SimulationService);
                }
                return data.UpdateSim;
            }

            saveSimulationRef.current = getClient().graphql({
                query: graphQlMutations.CreateSim,
                variables: {
                    request,
                    sandbox_id,
                },
            });

            const { data, errors } = await saveSimulationRef.current;

            // publish the CreateSim service latency metrics
            publishLatencyMetric(MetricName.CREATE_SIMULATION_LATENCY, measureLatency(startTime), ServiceType.SimulationService);

            // in case of CreateSim querry failure, publish the logs and metrics to cloudwatch
            if (errors?.length) {
                // publishing the error logs
                publishErrorLog(errors.map((error) => formatGraphQLError(error)));

                // publish the failure metrics
                publishFailureMetric(MetricName.CREATE_SIMULATION_ERROR_COUNT, ServiceType.SimulationService);
            }

            return data.CreateSim;
        },
    });
};

export const useSubmitSimulation = (): UseMutationResult<Sim, Error, { sandbox_id: string; sim_id: string; userAlias: string }> => {
    const { publishErrorLog } = useLogger();

    const { publishFailureMetric, publishLatencyMetric } = useMetrics();

    return useMutation<Sim, Error, { sandbox_id: string; sim_id: string; userAlias: string }>({
        mutationFn: async ({ sandbox_id, sim_id, userAlias }) => {
            const startTime = Date.now();

            const { data, errors }: GraphQLResult<any> = await getClient().graphql({
                query: graphQlMutations.StartSim,
                variables: {
                    sandbox_id,
                    sim_id,
                    userAlias,
                },
            });

            // publish the StartSim service latency metrics
            publishLatencyMetric(MetricName.START_SIMULATION_LATENCY, measureLatency(startTime), ServiceType.SimulationService);

            // in case of StartSim mutation failure, publish the logs and metrics to cloudwatch
            if (errors?.length) {
                // publishing the error logs
                publishErrorLog(errors.map((error) => formatGraphQLError(error)));

                // publish the failure metrics
                publishFailureMetric(MetricName.START_SIMULATION_ERROR_COUNT, ServiceType.SimulationService);
            }
            return data.StartSim;
        },
    });
};

export const useCancelSimulation = (): UseMutationResult<Sim, Error, { sandbox_id: string; sim_id: string; userAlias: string }> => {
    const { publishErrorLog } = useLogger();

    const { publishFailureMetric, publishLatencyMetric } = useMetrics();

    return useMutation<Sim, Error, { sandbox_id: string; sim_id: string; userAlias: string }>({
        mutationFn: async ({ sandbox_id, sim_id, userAlias }) => {
            const startTime = Date.now();

            const { data, errors }: GraphQLResult<any> = await getClient().graphql({
                query: graphQlMutations.CancelSim,
                variables: {
                    sandbox_id,
                    sim_id,
                    userAlias,
                },
            });

            // publish the CancelSim service latency metrics
            publishLatencyMetric(MetricName.CANCEL_SIMULATION_LATENCY, measureLatency(startTime), ServiceType.SimulationService);

            // in case of CancelSim mutation failure, publish the logs and metrics to cloudwatch
            if (errors?.length) {
                // publishing the error logs
                publishErrorLog(errors.map((error) => formatGraphQLError(error)));

                // publish the failure metrics
                publishFailureMetric(MetricName.CANCEL_SIMULATION_ERROR_COUNT, ServiceType.SimulationService);
            }

            return data.CancelSim;
        },
    });
};

export const useDeleteSimulation = (): UseMutationResult<
    Sim,
    Error,
    { sandbox_id: string; sim_id: string; is_shared: boolean; userAlias: string }
> => {
    const { publishErrorLog } = useLogger();

    const { publishFailureMetric, publishLatencyMetric } = useMetrics();

    return useMutation<Sim, Error, { sandbox_id: string; sim_id: string; is_shared: boolean; userAlias: string }>({
        mutationFn: async ({ sandbox_id, sim_id, is_shared, userAlias }) => {
            const startTime = Date.now();

            const { data, errors }: GraphQLResult<any> = await getClient().graphql({
                query: graphQlMutations.DeprecateSim,
                variables: {
                    sandbox_id,
                    sim_id,
                    is_shared,
                    userAlias,
                },
            });

            // publish the DeprecateSim service latency metrics
            publishLatencyMetric(MetricName.DEPRECATE_SIMULATION_LATENCY, measureLatency(startTime), ServiceType.SimulationService);

            // in case of DeprecateSim mutation failure, publish the logs and metrics to cloudwatch
            if (errors?.length) {
                // publishing the error logs
                publishErrorLog(errors.map((error) => formatGraphQLError(error)));

                // publish the failure metrics
                publishFailureMetric(MetricName.DEPRECATE_SIMULATION_ERROR_COUNT, ServiceType.SimulationService);
            }

            return data.DeprecateSim;
        },
    });
};

export const useGetEmbedUrlForSimulationReport = (
    user_id: string,
    sim_id: string,
    allocation_type: AllocationType,
    sandbox_id: string,
    is_shared: boolean = false,
): UseQueryResult<string, Error> => {
    const { publishErrorLog } = useLogger();

    const { publishFailureMetric, publishLatencyMetric } = useMetrics();

    return useQuery<string, Error>({
        queryKey: ["embedUrlForSimuationReport", user_id, sim_id, sandbox_id, is_shared],
        queryFn: async () => {
            const startTime = Date.now();

            const { data, errors }: GraphQLResult<any> = await getClient().graphql({
                query: graphQlQueries.GetReportEmbedUrl,
                variables: {
                    user_id,
                    sim_id,
                    allocation_type,
                    sandbox_id,
                    is_shared,
                },
            });

            // publish the GetReportEmbedUrl service latency metrics
            publishLatencyMetric(
                MetricName.GET_EMBED_URL_FOR_SIMULATION_REPORT_LATENCY,
                measureLatency(startTime),
                ServiceType.SimulationService,
            );

            // in case of GetReportEmbedUrl querry failure, publish the logs and metrics to cloudwatch
            if (errors?.length) {
                // publishing the error logs
                publishErrorLog(errors.map((error) => formatGraphQLError(error)));

                // publish the failure metrics
                publishFailureMetric(MetricName.GET_EMBED_URL_FOR_SIMULATION_REPORT_ERROR_COUNT, ServiceType.SimulationService);
            }

            return data.GetReportEmbedUrl;
        },
        gcTime: 1,
        enabled: !!user_id && !!sim_id?.length && !!allocation_type && !!sandbox_id,
    });
};

export const useCreateInputLink = (): UseMutationResult<Sim, Error, CreateLinkInputMutationVariables> => {
    const { publishErrorLog } = useLogger();

    const { publishFailureMetric, publishLatencyMetric } = useMetrics();

    return useMutation<Sim, Error, CreateLinkInputMutationVariables>({
        mutationFn: async ({ name, configs, userAlias, s3_path, sim_id, sandbox_id }) => {
            const startTime = Date.now();

            const { data, errors }: GraphQLResult<any> = await getClient().graphql({
                query: graphQlMutations.CreateLinkInput,
                variables: {
                    name,
                    configs,
                    userAlias,
                    s3_path,
                    sim_id,
                    sandbox_id,
                    inputType: InputType.manual_journal_entry,
                },
            });

            // publish the CreateLinkInput service latency metrics
            publishLatencyMetric(MetricName.CREATE_INPUT_LINK_LATENCY, measureLatency(startTime), ServiceType.SimulationService);

            // in case of CreateLinkInput querry failure, publish the logs and metrics to cloudwatch
            if (errors?.length) {
                // publishing the error logs
                publishErrorLog(errors.map((error) => formatGraphQLError(error)));

                // publish the failure metrics
                publishFailureMetric(MetricName.CREATE_INPUT_LINK_ERROR_COUNT, ServiceType.SimulationService);
            }

            return data.CreateLinkInput;
        },
    });
};

export const useShareSimToRFA = (): UseMutationResult<string | null, Error, ShareSimToRFAMutationVariables> => {
    const { publishErrorLog } = useLogger();
    const { publishFailureMetric, publishLatencyMetric } = useMetrics();

    return useMutation<string | null, Error, ShareSimToRFAMutationVariables>({
        mutationFn: async ({ sandbox_id, sim_id, userAlias, comments, simulation_tag }) => {
            const startTime = Date.now();

            const { data, errors }: GraphQLResult<any> = await getClient().graphql({
                query: graphQlMutations.ShareSimToRFA,
                variables: {
                    sandbox_id,
                    sim_id,
                    userAlias,
                    comments,
                    simulation_tag,
                },
            });

            // publish the ShareSimToRFA service latency metrics
            publishLatencyMetric(MetricName.SHARE_SIM_TO_RFA_LATENCY, measureLatency(startTime), ServiceType.SimulationService);

            // in case of ShareSimToRFA mutation failure, publish the logs and metrics to cloudwatch
            if (errors?.length) {
                // publishing the error logs
                publishErrorLog(errors.map((error) => formatGraphQLError(error)));

                // publish the failure metrics
                publishFailureMetric(MetricName.SHARE_SIM_TO_RFA_ERROR_COUNT, ServiceType.SimulationService);
            }

            return data.ShareSimToRFA;
        },
    });
};

export interface TriggerCommentaryGenerationResponse {
    task_arn: string | null;
    accepted: boolean;
}

const TRIGGER_COMMENTARY_GENERATION_MUTATION = /* GraphQL */ `
    mutation TriggerCommentaryGeneration($sandbox_id: String!, $sim_id: String!) {
        TriggerCommentaryGeneration(sandbox_id: $sandbox_id, sim_id: $sim_id) {
            task_arn
            accepted
        }
    }
`;

export const useTriggerCommentaryGeneration = (): UseMutationResult<
    TriggerCommentaryGenerationResponse,
    Error,
    { sandbox_id: string; sim_id: string }
> => {
    const { publishErrorLog } = useLogger();
    const { publishFailureMetric, publishLatencyMetric } = useMetrics();
    const queryClient = useQueryClient();

    return useMutation<TriggerCommentaryGenerationResponse, Error, { sandbox_id: string; sim_id: string }>({
        mutationFn: async ({ sandbox_id, sim_id }) => {
            const startTime = Date.now();

            const { data, errors }: GraphQLResult<any> = await getClient().graphql({
                query: TRIGGER_COMMENTARY_GENERATION_MUTATION,
                variables: { sandbox_id, sim_id },
            });

            publishLatencyMetric(
                MetricName.TRIGGER_COMMENTARY_GENERATION_LATENCY,
                measureLatency(startTime),
                ServiceType.SimulationService,
            );

            if (errors?.length) {
                publishErrorLog(errors.map((error) => formatGraphQLError(error)));
                publishFailureMetric(MetricName.TRIGGER_COMMENTARY_GENERATION_ERROR_COUNT, ServiceType.SimulationService);
                // Throw so react-query treats this as a failed mutation:
                // onError fires and rolls back the optimistic IN_PROGRESS
                // patch. Otherwise the UI would sit at "Generating…" forever
                // even though no Fargate task was launched (permission denied,
                // DDB conditional check failed for a duplicate click, etc.).
                throw new Error(errors.map((error) => formatGraphQLError(error)).join("; "));
            }

            return data?.TriggerCommentaryGeneration ?? { task_arn: null, accepted: false };
        },
        // onMutate fires SYNCHRONOUSLY the moment .mutate() is called — before
        // the network request even begins. That's what makes the UI flip to
        // IN_PROGRESS instantly, with zero visible delay. onSuccess/onError
        // would fire after the network round-trip (~200ms+).
        //
        // We snapshot the pre-mutation cache state and return it as context
        // so onError can roll back if the mutation itself fails (network
        // error, permission denied, DDB conditional check failed, etc.) —
        // otherwise the UI would be stuck at IN_PROGRESS despite no Fargate
        // task actually running.
        onMutate: (variables) => {
            const patchSim = (existing: any): any => {
                if (!existing) return existing;
                return { ...existing, commentary_status: CommentaryStatus.IN_PROGRESS };
            };

            // Snapshot for potential rollback.
            const previousSim = queryClient.getQueriesData({
                queryKey: ["simulation", variables.sandbox_id, variables.sim_id],
            });
            const previousList = queryClient.getQueriesData({
                queryKey: ["sandboxList", variables.sandbox_id],
            });

            // Patch every per-sim query (Sim Result page, per-row hooks).
            queryClient.setQueriesData(
                { queryKey: ["simulation", variables.sandbox_id, variables.sim_id] },
                patchSim,
            );

            // Seed the non-shared per-sim key from sandboxList when no per-sim
            // hook has been observed yet, so a subsequent hook activation
            // (e.g. after click, the History cell mounts its useGetSimulation)
            // reads primed cache instead of firing GetSimDetails. When we do
            // seed a new key, track it in the context so onError can REMOVE
            // (not restore) it on failure — getQueriesData snapshotted before
            // the seed will not contain this key, so restoring via previousSim
            // alone would leave a phantom IN_PROGRESS entry in the cache.
            const nonSharedKey = ["simulation", variables.sandbox_id, variables.sim_id, false] as const;
            let seededKey: readonly unknown[] | null = null;
            if (!queryClient.getQueryData(nonSharedKey)) {
                let simFromList: Sim | undefined;
                const listData = queryClient.getQueriesData({ queryKey: ["sandboxList", variables.sandbox_id] });
                for (const [, data] of listData) {
                    const pages = (data as any)?.pages ?? [];
                    for (const page of pages) {
                        const found = (page?.items ?? []).find((s: Sim) => s?.resource_id === variables.sim_id);
                        if (found) {
                            simFromList = found;
                            break;
                        }
                    }
                    if (simFromList) break;
                }
                if (simFromList) {
                    queryClient.setQueryData(nonSharedKey, patchSim(simFromList));
                    seededKey = nonSharedKey;
                }
            }

            // Patch the sandboxList item in place so the History row reflects
            // IN_PROGRESS without refetching every other row.
            queryClient.setQueriesData(
                { queryKey: ["sandboxList", variables.sandbox_id] },
                (existing: any) => {
                    if (!existing?.pages) return existing;
                    return {
                        ...existing,
                        pages: existing.pages.map((page: any) => ({
                            ...page,
                            items: (page.items ?? []).map((item: Sim) =>
                                item?.resource_id === variables.sim_id ? patchSim(item) : item,
                            ),
                        })),
                    };
                },
            );

            // Returned context is passed to onError for rollback.
            return { previousSim, previousList, seededKey };
        },
        onError: (_error, _variables, context: any) => {
            // Roll the cache back so the UI doesn't lie about IN_PROGRESS
            // when the mutation actually failed (permission denied, DDB
            // conditional check failed for a concurrent duplicate click,
            // network error, etc.).
            if (context?.previousSim) {
                for (const [key, value] of context.previousSim) {
                    queryClient.setQueryData(key, value);
                }
            }
            if (context?.previousList) {
                for (const [key, value] of context.previousList) {
                    queryClient.setQueryData(key, value);
                }
            }
            // The seeded key did not exist before onMutate ran, so it's not
            // in previousSim above. Remove it explicitly — otherwise a stale
            // IN_PROGRESS entry lingers in the cache and any subsequent
            // useGetSimulation activation reads it as truth.
            if (context?.seededKey) {
                queryClient.removeQueries({ queryKey: context.seededKey as readonly unknown[], exact: true });
            }
        },
    });
};

export interface SimulationCommentary {
    simulation_id: string;
    run_id: string | null;
    run_url: string | null;
    summary: string | null;
}

const GET_SIMULATION_COMMENTARY_QUERY = /* GraphQL */ `
    query GetSimulationCommentary($simulation_id: String!) {
        GetSimulationCommentary(simulation_id: $simulation_id) {
            simulation_id
            run_id
            run_url
            summary
        }
    }
`;

export const useGetSimulationCommentary = (simulation_id: string, enabled: boolean = true): UseQueryResult<SimulationCommentary | null, Error> => {
    const { publishErrorLog } = useLogger();
    const { publishFailureMetric, publishLatencyMetric } = useMetrics();

    return useQuery<SimulationCommentary | null, Error>({
        queryKey: ["GetSimulationCommentary", simulation_id],
        queryFn: async () => {
            const startTime = Date.now();

            const { data, errors }: GraphQLResult<any> = await getClient().graphql({
                query: GET_SIMULATION_COMMENTARY_QUERY,
                variables: { simulation_id },
            });

            publishLatencyMetric(MetricName.GET_SIMULATION_COMMENTARY_LATENCY, measureLatency(startTime), ServiceType.SimulationService);

            if (errors?.length) {
                publishErrorLog(errors.map((error) => formatGraphQLError(error)));
                publishFailureMetric(MetricName.GET_SIMULATION_COMMENTARY_ERROR_COUNT, ServiceType.SimulationService);
            }

            return data?.GetSimulationCommentary ?? null;
        },
        enabled: enabled && !!simulation_id?.length,
    });
};

export const usePublishSim = (): UseMutationResult<string | null, Error, PublishSimMutationVariables> => {
    const { publishErrorLog } = useLogger();
    const { publishFailureMetric, publishLatencyMetric } = useMetrics();

    return useMutation<string | null, Error, PublishSimMutationVariables>({
        mutationFn: async ({ sandbox_id, sim_id, userAlias }) => {
            const startTime = Date.now();

            const { data, errors }: GraphQLResult<any> = await getClient().graphql({
                query: graphQlMutations.PublishSim,
                variables: {
                    sandbox_id,
                    sim_id,
                    userAlias,
                },
            });

            // publish the PublishSim service latency metrics
            publishLatencyMetric(MetricName.PUBLISH_SIM_LATENCY, measureLatency(startTime), ServiceType.SimulationService);

            // in case of PublishSim mutation failure, publish the logs and metrics to cloudwatch
            if (errors?.length) {
                // publishing the error logs
                publishErrorLog(errors.map((error) => formatGraphQLError(error)));

                // publish the failure metrics
                publishFailureMetric(MetricName.PUBLISH_SIM_ERROR_COUNT, ServiceType.SimulationService);
            }

            return data.PublishSim;
        },
    });
};
